import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';

export interface LocalStorageData {
  privacyArticle: {
    lastKnownIP?: string;
    lastKnownCity?: string;
    lastKnownCountry?: string;
    lastKnownLatLong?: [number, number];
    referrers?: string[];
    lastSearchParamEntity?:
      | 'amit'
      | 'pauline'
      | 'zdenek'
      | 'techsquat'
      | 'vladimir'
      | 'theDrinkingChat'
      | 'david'
      | 'hanna'
      | 'marta';
  };
}

export const localStorageEvent =
  typeof window !== 'undefined' && new CustomEvent('localStorage', {});

export const useLocalStorage = <T extends keyof LocalStorageData>(
  key: T,
  defaultValue: LocalStorageData[T]
): [
  LocalStorageData[T],
  React.Dispatch<React.SetStateAction<LocalStorageData[T]>>,
] => {
  const getValue = () => {
    try {
      if (typeof window === 'undefined') return defaultValue;

      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.log(error);
      return defaultValue;
    }
  };

  const [storedValue, setStoredValue] =
    useState<LocalStorageData[T]>(getValue());

  useEffect(() => {
    try {
      const value = getValue();

      if (isEqual(value, storedValue)) return;

      localStorage.setItem(key, JSON.stringify(storedValue));

      if (localStorageEvent) {
        document.dispatchEvent(localStorageEvent);
      }
    } catch (error) {
      console.log(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- writes when the value changes; key is fixed for the hook lifetime
  }, [storedValue]);

  useEffect(() => {
    const storageHandler = () => {
      setStoredValue(getValue());
    };

    document.addEventListener('localStorage', storageHandler);

    return () => {
      document.removeEventListener('localStorage', storageHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- subscribes once on mount; getValue reads storage, not state
  }, []);

  return [storedValue, setStoredValue];
};
